 import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "./assets/logo.png";
import NotificationSnackbar from "./NotificationBar";

const SurveyResponsesView = () => {
  const { surveyId } = useParams();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveyInfo, setSurveyInfo] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Color scheme
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const BACKGROUND_COLOR = "linear-gradient(195deg, #0a2e52 0%, #1565C0 100%)";

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cleanSurveyId = surveyId.split(':')[0];
        
        // Fetch survey details and responses in parallel
        const [surveyRes, responsesRes, categoriesRes] = await Promise.all([
          fetch("/api/support/surveys"),
          fetch(`/api/support/survey-responses/${cleanSurveyId}`),
          fetch("/api/support/categories")
        ]);

        if (!responsesRes.ok) throw new Error('Failed to fetch survey responses');
        if (!surveyRes.ok) throw new Error('Failed to fetch survey details');

        const [surveysData, responsesData, categoriesData] = await Promise.all([
          surveyRes.json(),
          responsesRes.json(),
          categoriesRes.json()
        ]);

        console.log("Survey Data:", surveysData);
        console.log("Responses Data:", responsesData);
        console.log("Categories Data:", categoriesData);

        // Find the current survey details
        const currentSurvey = surveysData.surveys.find(s => s.id === cleanSurveyId);
        
        if (currentSurvey) {
          setSurveyInfo({
            title: currentSurvey.title,
            description: currentSurvey.descriptions
          });
        }

        if (responsesData.length > 0) {
          // Transform the responses data to match your frontend structure
          const transformedResponses = responsesData.map(response => ({
            respondentId: response.Id || "anonymous",
            respondentName: "Question " + (response.Id || "Anonymous"),
            submissionDate: response.CreatedOn || new Date().toISOString(),
            responses: [{
              questionId: response.QuestionID,
              questionText: `Question ${response.QuestionID}`,
              response: response.ResponseText,
              questionType: "text" // Default type, adjust as needed
            }]
          }));

          setResponses(transformedResponses);
        } else {
          setResponses([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        handleSnackbarOpen("Failed to load survey data", "error");
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchData();
    }
  }, [surveyId]);

  const handleCloseViewer = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: BACKGROUND_COLOR,
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        width: "100%",
        background: BACKGROUND_COLOR,
        padding: { xs: "6px", sm: "5px" },
        boxSizing: "border-box",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          mt: { xs: 0, sm: "60px" },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            padding: { xs: "20px 16px", sm: "32px" },
            borderRadius: "16px",
            backgroundColor: "#fff",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #3b82f6, #1e40af)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={logo}
                alt="Logo"
                style={{
                  width: "48px",
                  height: "48px",
                  marginRight: "12px",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #1e40af, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.5px",
                }}
              >
                Survey Responses
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseViewer}
              aria-label="close"
              sx={{
                color: "#64748b",
                "&:hover": {
                  backgroundColor: "rgba(100, 116, 139, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              marginBottom: "24px",
              padding: "24px",
              borderLeft: `4px solid ${PRIMARY_COLOR}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                marginBottom: "12px",
                color: PRIMARY_COLOR,
              }}
            >
              {surveyInfo.title || "Survey Responses"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#5f6368",
                lineHeight: "1.6",
                fontSize: "1rem",
              }}
            >
              {surveyInfo.description || "View all responses for this survey"}
            </Typography>
            <Chip
              label={`${responses.length} ${responses.length === 1 ? 'response' : 'responses'}`}
              color="primary"
              sx={{ mt: 2 }}
            />
          </Box>

          {responses.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                No responses found for this survey
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
              {responses.map((respondent, index) => (
                <Accordion
                  key={respondent.respondentId}
                  defaultExpanded={index === 0}
                  sx={{
                    mb: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: "8px !important",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {respondent.respondentName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                        {new Date(respondent.submissionDate).toLocaleString()}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                            <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Response</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {respondent.responses.map((response, idx) => (
                            <TableRow
                              key={`${respondent.respondentId}-${response.questionId}-${idx}`}
                              sx={{
                                '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' },
                                '&:last-child td': { borderBottom: 0 }
                              }}
                            >
                              <TableCell>
                                <Typography sx={{ fontWeight: 500 }}>
                                  {response.questionText}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {response.questionType === 'multiple choice' ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {response.response.split(', ').map((item, i) => (
                                      <Chip key={i} label={item} size="small" />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography>
                                    {response.response || <em>No response</em>}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default SurveyResponsesView;